export interface Disposable {
  dispose: () => void
}

export interface DisposerBag {
  /** Registers `thing` for cleanup and returns it unchanged, so it can be chained inline where it's created. */
  track: <T extends Disposable>(thing: T) => T
  /** Registers a raw cleanup callback (event listeners, observers) alongside tracked objects. */
  defer: (cleanup: () => void) => void
  /** Runs every registered cleanup in reverse registration order, swallowing errors from any one of them — a scene may already be mid-teardown when this runs. */
  disposeAll: () => void
}

/**
 * The `disposers`/`track()` bag every scene builder in this feature
 * hand-rolled slightly differently (buildRealisticHall, buildSpacesRoom,
 * buildMountainLot). One shared version so cleanup ordering/error-handling
 * doesn't drift between them, and so a builder that rebuilds in place (like
 * SpacesStudio's preset switch) can call `disposeAll()` and keep using the
 * same bag for the next build.
 */
export function createDisposerBag(): DisposerBag {
  let disposers: (() => void)[] = []
  return {
    track: (thing) => {
      disposers.push(() => thing.dispose())
      return thing
    },
    defer: (cleanup) => {
      disposers.push(cleanup)
    },
    disposeAll: () => {
      const toRun = disposers.reverse()
      disposers = []
      toRun.forEach((d) => {
        try {
          d()
        } catch {
          // scene may already be tearing down
        }
      })
    },
  }
}
